import React from "react";
import ScrollModal from "@/components/common/scroll-modal";
import {
  TextField,
  Icon,
  Button,
  Heading,
  GroupTextField,
  GroupTextFieldRow,
  Input,
  Switch,
  Radiobutton,
  Badge,
} from "@elaniintc/goes-design-system";
import SelectField from "@/components/common/select";
import { OptionTooltip } from "@/workflow/components/configurations/form/components/OptionTooltip";
import { parseCharacters } from "@/utils/format";
import { useFieldForm } from "@/pages/digital-files/entity/hooks/useFieldForm";
import type {
  EntityFieldType,
  FieldConfig,
} from "@/pages/digital-files/entity/config/fieldConfigurations";
import type { EntityField } from "../../types";
import { DataCenterField } from "../components/DataCenterField";
import { ALLOWED_FILE_TYPES } from "../../utils";
import { DataCenterDataSelect } from "../../components/DataCenterDataSelect";
import { cn } from "@/utils/theme";

const fieldTypeLabels: Record<string, string> = {
  text: "Texto",
  number: "Números",
  textarea: "Área de texto",
  date: "Fecha y hora",
  date_range: "Rango de fechas",
  select: "Lista de selección",
  email: "Correo electrónico",
  phone: "Número telefónico",
  file: "Subida de archivos",
  identification: "Identificación",
  interoperability: "Interoperabilidad",
};

interface FieldConfigModalProps {
  open: boolean;
  type: string | null;
  sectionId: string | null;
  isCreateMode?: boolean;
  initialField?: EntityField;
  onClose: () => void;
  onSave: (data: EntityField) => void;
}

const FieldConfigModal: React.FC<FieldConfigModalProps> = ({
  open,
  type,
  sectionId,
  onClose,
  onSave,
  initialField,
  isCreateMode = true,
}) => {
  // Usar el custom hook para manejar el estado del formulario
  const {
    fieldName,
    keyValue,
    formValues,
    localListItems,
    newListItem,
    newFileType,
    locked,
    isValid,
    fieldConfig,
    errors,
    isNew,
    setFieldName,
    setKeyValue,
    updateFormValue,
    setLocalListItems,
    setNewListItem,
    setNewFileType,
    setLocked,
    reset,
    setDataCenterValue,
    getFieldData,
    validate,
  } = useFieldForm({
    type: type as EntityFieldType | null,
    initialField,
    isOpen: open,
  });

  const handleSave = () => {
    if (!type || !sectionId) return;

    // Validar antes de guardar
    const isValidated = validate();
    if (!isValidated) {
      return;
    }

    const fieldData = getFieldData();
    if (fieldData) {
      onSave(fieldData);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFieldNameChange = (value: string) => {
    setFieldName(value);
    if (!initialField?.id && locked) {
      setKeyValue(parseCharacters(value.toLowerCase()));
    }
  };

  const handleKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyValue(e.target.value);
  };

  // Renderizar un campo individual según su tipo
  const renderField = (fieldConfig: FieldConfig) => {
    const value = formValues[fieldConfig.key];

    switch (fieldConfig.type) {
      case "text":
        return (
          <TextField
            key={fieldConfig.key}
            label={fieldConfig.label}
            placeholder={fieldConfig.placeholder}
            required={fieldConfig.required}
            value={
              typeof value === "string" || typeof value === "number"
                ? value
                : ""
            }
            onChange={(e) => updateFormValue(fieldConfig.key, e.target.value)}
            error={
              fieldConfig.validation?.custom
                ? fieldConfig.validation.custom(value) || undefined
                : undefined
            }
            helperText={fieldConfig.helperText}
            disabled={fieldConfig.disabled}
          />
        );

      case "switch": {
        const switchTrueText = fieldConfig.customProps?.switchTrueText || "Si";
        const switchFalseText =
          fieldConfig.customProps?.switchFalseText || "No";

        return (
          <div key={fieldConfig.key} className="flex flex-col gap-1">
            <label className="font-semibold text-body-loud leading-2xl">
              {fieldConfig.label}
              {fieldConfig.required && <span className="text-danger">*</span>}
            </label>
            <div className="flex items-center gap-3 mt-1">
              <Switch
                checked={typeof value === "boolean" ? value : false}
                onCheckedChange={(checked) =>
                  updateFormValue(fieldConfig.key, checked)
                }
                disabled={fieldConfig.disabled}
              />
              <span className="text-body font-normal">
                {value ? switchTrueText : switchFalseText}
              </span>
            </div>
            {fieldConfig.helperText && (
              <p className="text-xs text-body-quiet mt-1">
                {fieldConfig.helperText}
              </p>
            )}
          </div>
        );
      }

      case "select": {
        const fieldSource = fieldConfig?.customProps?.source;
        const labelSuffix = fieldConfig.customProps?.labelSuffix;
        if (fieldSource == "options_root") {
          const isSearchable = fieldConfig.customProps?.searchable === true;
          const optionsRootSource = formValues["options_root_source"];
          let localList: string[] = [];
          let databaseConfig: { table: string; column: string } | undefined =
            undefined;
          if (optionsRootSource === "manual") {
            const localListValues = formValues["options_root_list"];
            localList =
              localListValues &&
              Array.isArray(localListValues) &&
              localListValues.length > 0 &&
              typeof localListValues[0] === "string"
                ? (localListValues as string[])
                : [];
          } else {
            const table = formValues["options_root_datacenter_table"];
            const column = formValues["options_root_datacenter_column"];
            if (
              table &&
              column &&
              typeof table === "string" &&
              typeof column === "string"
            ) {
              databaseConfig = { table, column };
            }
          }
          // current value
          return (
            <DataCenterDataSelect
              key={fieldConfig.key}
              name={fieldConfig.key}
              label={fieldConfig.label}
              placeholder={fieldConfig.placeholder ?? "Selecciona una opción"}
              required={fieldConfig.required}
              helperText={fieldConfig.helperText}
              labelHelperText={labelSuffix}
              enableSearch={isSearchable && !!databaseConfig}
              initOptions={localList}
              database={databaseConfig}
              value={formValues[fieldConfig.key] as string}
              onChange={(value: { key: string; label: string }) => {
                updateFormValue(fieldConfig.key, value.key);
              }}
            />
          );
        } else {
          const selectOptions = fieldConfig.options || [];
          const optionIcon = fieldConfig.customProps?.optionIcon;
          const optionIconPosition =
            fieldConfig.customProps?.optionIconPosition || "left";
          return (
            <div key={fieldConfig.key} className="flex flex-col gap-1">
              {labelSuffix ? (
                <div className="flex items-center justify-between">
                  <label className="font-semibold text-body-loud text-sm leading-2xl">
                    {fieldConfig.label}
                    {fieldConfig.required && (
                      <span className="text-sm text-danger ml-1">*</span>
                    )}
                  </label>
                  <span className="text-xs text-body-quiet">{labelSuffix}</span>
                </div>
              ) : null}
              <SelectField
                name={fieldConfig.key}
                label={labelSuffix ? "" : fieldConfig.label}
                placeholder={fieldConfig.placeholder || "Selecciona una opción"}
                required={fieldConfig.required}
                value={typeof value === "string" ? value : ""}
                onChange={(newValue) =>
                  updateFormValue(fieldConfig.key, newValue)
                }
                options={
                  selectOptions?.map((opt) => ({
                    value: opt.value,
                    label: opt.label,
                    icon: optionIcon ? (
                      <Icon
                        type="far"
                        name={optionIcon}
                        className="text-body-quiet text-base w-4"
                      />
                    ) : undefined,
                    iconPosition: optionIcon ? optionIconPosition : undefined,
                  })) || []
                }
                srOnlyLabel={!!labelSuffix}
              />
              {fieldConfig.helperText && (
                <p className="text-xs text-body-quiet mt-1">
                  {fieldConfig.helperText}
                </p>
              )}
            </div>
          );
        }
      }

      case "number": {
        const numberLabelSuffix = fieldConfig.customProps?.labelSuffix;

        return (
          <div key={fieldConfig.key} className="flex flex-col gap-1">
            {numberLabelSuffix ? (
              <div className="flex items-center justify-between">
                <label className="font-semibold text-body-loud text-sm leading-2xl">
                  {fieldConfig.label}
                  {fieldConfig.required && (
                    <span className="text-sm text-danger ml-1">*</span>
                  )}
                </label>
                <span className="text-xs text-body-quiet">
                  {numberLabelSuffix}
                </span>
              </div>
            ) : (
              <label className="font-semibold text-body-loud text-sm leading-2xl">
                {fieldConfig.label}
                {fieldConfig.required && (
                  <span className="text-sm text-danger ml-1">*</span>
                )}
              </label>
            )}
            <TextField
              label={numberLabelSuffix ? "" : ""}
              type="number"
              placeholder={fieldConfig.placeholder}
              className="w-full"
              value={value?.toString() || ""}
              onChange={(e) =>
                updateFormValue(
                  fieldConfig.key,
                  e.target.value ? Number(e.target.value) : "",
                )
              }
              disabled={fieldConfig.disabled}
            />
            {fieldConfig.helperText && (
              <p className="text-xs text-body-quiet mt-1">
                {fieldConfig.helperText}
              </p>
            )}
          </div>
        );
      }

      case "multiselect": {
        const selectedValues = Array.isArray(value) ? value : [];

        return (
          <div key={fieldConfig.key} className="flex flex-col gap-1">
            <label className="font-semibold text-body-loud leading-2xl">
              {fieldConfig.label}
              {fieldConfig.required && <span className="text-danger">*</span>}
            </label>
            <div className="space-y-2 mt-1">
              {fieldConfig.options?.map((option) => {
                const isChecked = selectedValues.includes(option.value);
                return (
                  <div key={option.value} className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id={`${fieldConfig.key}-${option.value}`}
                      checked={isChecked}
                      onChange={(e) => {
                        const newValues = e.target.checked
                          ? [...selectedValues, option.value]
                          : selectedValues.filter((v) => v !== option.value);
                        updateFormValue(fieldConfig.key, newValues);
                      }}
                      className="w-4 h-4 rounded border-neutral-quiet"
                    />
                    <label
                      htmlFor={`${fieldConfig.key}-${option.value}`}
                      className="text-body text-sm cursor-pointer"
                    >
                      {option.label}
                    </label>
                  </div>
                );
              })}
            </div>
            {fieldConfig.helperText && (
              <p className="text-xs text-body-quiet mt-1">
                {fieldConfig.helperText}
              </p>
            )}
          </div>
        );
      }

      case "radio": {
        return (
          <div key={fieldConfig.key} className="flex flex-col gap-1">
            <label className="font-semibold text-body-loud leading-2xl">
              {fieldConfig.label}
              {fieldConfig.required && <span className="text-danger">*</span>}
            </label>
            <div className="space-y-2 mt-1">
              {fieldConfig.options?.map((option) => (
                <div key={option.value} className="flex items-center gap-3">
                  <Radiobutton
                    id={`${fieldConfig.key}-${option.value}`}
                    name={fieldConfig.key}
                    value={option.value}
                    checked={value === option.value}
                    onChange={() =>
                      updateFormValue(fieldConfig.key, option.value)
                    }
                  />
                  <label
                    htmlFor={`${fieldConfig.key}-${option.value}`}
                    className="text-body text-sm cursor-pointer"
                  >
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
            {fieldConfig.helperText && (
              <p className="text-xs text-body-quiet mt-1">
                {fieldConfig.helperText}
              </p>
            )}
          </div>
        );
      }

      case "local_list": {
        // localList (dropdown button) usa localListItems, otros usan formValues con su key
        const isDropdownList = fieldConfig.key === "localList";
        const currentListItems = isDropdownList
          ? localListItems
          : Array.isArray(formValues[fieldConfig.key])
            ? (formValues[fieldConfig.key] as string[])
            : [];

        const handleAddItem = () => {
          if (newListItem.trim()) {
            if (isDropdownList) {
              // Para dropdown button: solo actualizar localListItems, NO formValues
              setLocalListItems([...currentListItems, newListItem.trim()]);
            } else {
              // Para campo select/local_list: actualizar formValues
              const updatedItems = [...currentListItems, newListItem.trim()];
              updateFormValue(fieldConfig.key, updatedItems);
            }
            setNewListItem("");
          }
        };

        const handleRemoveItem = (index: number) => {
          const updatedItems = currentListItems.filter((_, i) => i !== index);
          if (isDropdownList) {
            // Para dropdown button: solo actualizar localListItems, NO formValues
            setLocalListItems(updatedItems);
          } else {
            // Para campo select/local_list: actualizar formValues
            updateFormValue(fieldConfig.key, updatedItems);
          }
        };

        return (
          <div key={fieldConfig.key} className="flex flex-col gap-1">
            <label className="font-semibold text-body-loud leading-2xl">
              {fieldConfig.label}
              {fieldConfig.required && <span className="text-danger">*</span>}
            </label>
            <div className="flex items-end pt-4">
              <div className="w-full">
                <TextField
                  label=""
                  placeholder="Añade un nuevo item"
                  className="rounded-tr-none rounded-br-none"
                  value={newListItem}
                  onChange={(e) => setNewListItem(e.target.value)}
                />
              </div>
              <Button
                className="mb-1 h-11 rounded-bl-none rounded-tl-none border-l-0"
                variant="quiet"
                onClick={handleAddItem}
              >
                <Icon name="plus" type="far" />
              </Button>
            </div>
            {fieldConfig.helperText && (
              <p className="text-xs mt-1 text-info">
                <Icon
                  name="circle-info"
                  type="far"
                  size="sm"
                  className="inline-block mr-2"
                />
                {fieldConfig.helperText}
              </p>
            )}
            {currentListItems.length > 0 && (
              <div className="w-full max-w-2xl rounded-xl border border-neutral bg-neutral-object shadow-sm backdrop-blur p-1 mt-2">
                <ul className="px-2">
                  {currentListItems.map((item: string, index: number) => (
                    <li
                      key={index}
                      className="flex items-center justify-between py-1"
                    >
                      <span className="text-base text-body-quiet">{item}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        type="button"
                        aria-label="Eliminar"
                        className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-full text-body-quiet"
                        onClick={() => handleRemoveItem(index)}
                      >
                        <Icon name="xmark" type="far" />
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      }

      case "file_types": {
        // Usar el valor de formValues si existe, sino usar un array vacío
        const currentFileTypes = Array.isArray(formValues[fieldConfig.key])
          ? (formValues[fieldConfig.key] as string[])
          : [];

        return (
          <div key={fieldConfig.key} className="flex flex-col gap-1">
            <label className="font-semibold text-body-loud leading-2xl">
              {fieldConfig.label}
              {fieldConfig.required && <span className="text-danger">*</span>}
            </label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <TextField
                  label=""
                  placeholder={
                    fieldConfig.placeholder || "Escribe una extensión"
                  }
                  className="w-full pr-10"
                  value={newFileType}
                  onChange={(e) => setNewFileType(e.target.value)}
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <OptionTooltip title="Extensiones permitidas: pdf, doc, docx, jpg, jpeg, png, gif, webp, svg, txt, csv, xls, xlsx, ppt, pptx, json, zip, rar, mpp, mpt, dwg, dwf, dxf">
                    <div className="text-body-quiet cursor-help">
                      <Icon name="circle-info" type="far" className="text-sm" />
                    </div>
                  </OptionTooltip>
                </div>
              </div>
              <Button
                className="h-11"
                variant="quiet"
                disabled={
                  !newFileType.trim() ||
                  ALLOWED_FILE_TYPES.indexOf(
                    newFileType.trim().toLowerCase(),
                  ) === -1
                }
                onClick={() => {
                  if (newFileType.trim()) {
                    const updatedItems = [
                      ...currentFileTypes,
                      newFileType.trim(),
                    ];
                    updateFormValue(fieldConfig.key, updatedItems);
                    setNewFileType("");
                  }
                }}
              >
                <Icon name="plus" type="far" />
              </Button>
            </div>
            {currentFileTypes.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {currentFileTypes.map((fileType: string, index: number) => (
                  <Badge
                    key={index}
                    variant="action"
                    size="sm"
                    className="flex items-center gap-1.5 px-2 py-1 text-action"
                  >
                    <span>{fileType}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      type="button"
                      aria-label="Eliminar"
                      className="h-4 w-4 p-0 text-action"
                      onClick={() => {
                        const updatedItems = currentFileTypes.filter(
                          (_: string, i: number) => i !== index,
                        );
                        updateFormValue(fieldConfig.key, updatedItems);
                      }}
                    >
                      <Icon name="xmark" type="far" className="text-xs" />
                    </Button>
                  </Badge>
                ))}
              </div>
            )}
            {fieldConfig.helperText && (
              <p className="text-xs text-body-quiet mt-1">
                {fieldConfig.helperText}
              </p>
            )}
          </div>
        );
      }

      case "datacenter": {
        const disableSearch = fieldConfig.customProps?.disableSearch || false;
        const tableKey = `${fieldConfig.key}_table`;
        const columnKey = `${fieldConfig.key}_column`;
        return (
          <DataCenterField
            key={fieldConfig.key}
            fieldKey={fieldConfig.key}
            label={fieldConfig.label}
            placeholder={fieldConfig.placeholder}
            helperText={fieldConfig.helperText}
            required={fieldConfig.required}
            selectedTable={formValues[tableKey] as string}
            selectedColumn={formValues[columnKey] as string}
            onChange={setDataCenterValue}
            disableSearch={disableSearch}
          />
        );
      }

      case "default_value": {
        const helperTextIcon =
          fieldConfig.customProps?.helperTextIcon || "circle-info";
        const helperTextColorClass =
          fieldConfig.customProps?.helperTextColor ?? "text-body-quiet";

        return (
          <div key={fieldConfig.key} className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <label className="font-semibold text-body-loud leading-2xl">
                {fieldConfig.label}
              </label>
              {!fieldConfig.required && (
                <span className="text-xs text-body-quiet">Opcional</span>
              )}
            </div>
            <div className="flex items-center w-full relative">
              <Input
                className="w-full border-neutral-quiet"
                placeholder={fieldConfig.placeholder}
                value={typeof value === "string" ? value : ""}
                onChange={(e) =>
                  updateFormValue(fieldConfig.key, e.target.value)
                }
                disabled={fieldConfig.disabled}
              />
            </div>
            {fieldConfig.helperText && (
              <div className="flex items-center gap-1.5 mt-1">
                <Icon
                  name={helperTextIcon}
                  type="far"
                  className={`${helperTextColorClass} text-xs`}
                />
                <p className={`text-xs ${helperTextColorClass}`}>
                  {fieldConfig.helperText}
                </p>
              </div>
            )}
          </div>
        );
      }

      case "custom": {
        if (fieldConfig.customProps?.type === "info_tags") {
          const { title, tags } = fieldConfig.customProps;
          return (
            <div key={fieldConfig.key} className="flex flex-col gap-2">
              {title && (
                <p className="text-sm text-body font-normal">{title}</p>
              )}
              {tags && tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {tags.map(
                    (
                      tag: {
                        type: string;
                        label: string;
                        icon?: string | React.ReactNode;
                      },
                      index: number,
                    ) => (
                      <Badge
                        key={index}
                        variant="neutral"
                        size="sm"
                        className="text-body-quiet bg-neutral-object border border-neutral-quiet"
                      >
                        {tag.icon && (
                          <span className="mr-1">
                            {typeof tag.icon === "string" ? tag.icon : tag.icon}
                          </span>
                        )}
                        {tag.label}
                      </Badge>
                    ),
                  )}
                </div>
              )}
            </div>
          );
        }
        return null;
      }

      default:
        return null;
    }
  };

  return (
    <ScrollModal
      isOpen={open}
      onClose={handleClose}
      modalContentClassName="w-full max-w-[520px] h-full max-h-[680px] flex flex-col"
      header={
        <div className="flex items-center justify-between px-8 py-3">
          <Heading variant="h5" className="mb-0!">
            {type ? fieldTypeLabels[type] || "Campo" : "Modal"}
          </Heading>
          <Button
            type="button"
            onClick={handleClose}
            variant="quiet"
            aria-label="Cerrar"
            className="w-8 h-8 cursor-pointer"
          >
            <Icon type="far" name="times" className="text-sm text-body" />
          </Button>
        </div>
      }
      footer={
        <div className="w-full grid grid-cols-1 tablet:grid-cols-2 py-3 px-8 gap-3">
          <Button
            variant="tertiary"
            size="sm"
            type="button"
            onClick={handleClose}
            className="min-w-30 cursor-pointer"
          >
            Cancelar
          </Button>
          <Button
            variant="default"
            size="sm"
            type="button"
            className="min-w-35 cursor-pointer disabled:bg-neutral-object-accent disabled:text-on-disabled disabled:border-none disabled:shadow-none"
            disabled={!isValid || !type || !sectionId}
            onClick={handleSave}
          >
            Guardar
          </Button>
        </div>
      }
    >
      <div className="px-8.5 space-y-6 overflow-y-auto text-sm">
        {/* Campo especial: Nombre del campo y Key - siempre presente */}
        <Heading variant="h6" className="mb-4">
          Detalles
        </Heading>

        <GroupTextField
          label="Nombre del campo"
          required
          error={errors.fieldName}
        >
          <GroupTextFieldRow>
            <Input
              className="w-full"
              placeholder="Escribe el nombre del campo"
              value={fieldName}
              onChange={(e) => handleFieldNameChange(e.target.value)}
              required
            />
          </GroupTextFieldRow>
          <GroupTextFieldRow>
            <div className="flex items-center w-full relative">
              <Input
                className="w-full pr-20 rounded-br-md!"
                placeholder="Key"
                value={keyValue}
                disabled={locked}
                onChange={handleKeyChange}
                group="down-left"
              />
              <div
                className={cn(
                  "absolute top-0 bottom-0 flex items-center gap-2",
                  isCreateMode || isNew ? "right-0" : "right-3",
                )}
              >
                <OptionTooltip title="La key es el identificador técnico del campo. Se recomienda no modificarla si ya se ha utilizado en integraciones o automatizaciones.">
                  <div className="text-body-quiet cursor-help">
                    <Icon name="circle-info" type="far" />
                  </div>
                </OptionTooltip>
                {(isCreateMode || isNew) && (
                  <Button
                    variant="quiet"
                    type="button"
                    onClick={() => setLocked(!locked)}
                    aria-label={
                      locked
                        ? "Desbloquear edición de key"
                        : "Bloquear edición de key"
                    }
                    className="h-full px-3 bg-neutral-object border-l border-t border-b border-neutral-quiet rounded-br-md rounded-tl-none rounded-tr-none rounded-bl-none flex items-center justify-center"
                  >
                    <Icon
                      name={locked ? "lock" : "lock-open"}
                      type="far"
                      className="text-body-quiet text-sm"
                    />
                  </Button>
                )}
              </div>
            </div>
          </GroupTextFieldRow>
        </GroupTextField>

        {/* Renderizar secciones y campos dinámicamente desde la configuración */}
        {fieldConfig?.sections.map((section, sectionIndex) => {
          // Filtrar campos especiales que ya se muestran arriba
          const filteredFields = section.fields.filter(
            (field) => field.key !== "fieldName",
          );

          // Verificar qué campos deben mostrarse según sus condiciones
          const visibleFields = filteredFields.filter((field) => {
            if (!field.condition) return true;
            return field.condition(formValues);
          });

          // Si no hay campos visibles, no mostrar la sección
          if (visibleFields.length === 0) {
            return null;
          }

          return (
            <React.Fragment key={sectionIndex}>
              {(section.showSeparator || sectionIndex > 0) &&
                sectionIndex > 0 && (
                  <hr className="border-neutral-quiet my-6" />
                )}
              {sectionIndex > 0 && (
                <Heading variant="h6" className="mb-4">
                  {section.title}
                </Heading>
              )}
              {visibleFields.map((field) => renderField(field))}
            </React.Fragment>
          );
        })}
      </div>
    </ScrollModal>
  );
};

export default FieldConfigModal;
